import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { Component } from 'react';

import { DISPLAY_DATE_FORMAT_LONG_WITH_DAY, INPUT_DATE_FORMAT } from '../../../config/helpers';
import { itinerarySideBarDayPropTypes } from '../../../propTypes';
import formatPrice from '../../../util/formatPrice';

import breakpoints from '../../../config/breakpoints';
import {
  currencyWithPriceAndFormattedPriceArrayPropTypes,
  getInitialCurrencyState,
} from '../../../util/currency';
import AbandonBasketModal, { abandonBasketModalLabelProps } from '../AbandonBasketModal';
import Button from '../Atoms/Button';
import Icon, { IconSource } from '../Atoms/Icon';
import CUSTOM_EVENT from '../../../util/customEvents';

const propTypes = exact({
  bookNowButtonText: PropTypes.string,
  bookNowButtonUrl: PropTypes.string,
  buttonText: PropTypes.string,
  callToBookButtonText: PropTypes.string,
  dayText: PropTypes.string,
  fromDate: PropTypes.string.isRequired,
  toDate: PropTypes.string.isRequired,
  description: PropTypes.string,
  handleToggleClick: PropTypes.func,
  headerText: PropTypes.string,
  hideBookButton: PropTypes.bool,
  isCallToBook: PropTypes.bool,
  isSoldOut: PropTypes.bool,
  isUpdated: PropTypes.bool,
  itinerary: PropTypes.arrayOf(itinerarySideBarDayPropTypes).isRequired,
  pricePerCurrency: currencyWithPriceAndFormattedPriceArrayPropTypes,
  priceTotal: PropTypes.number,
  shareButtonText: PropTypes.string,
  shareEmailPackageBodyText: PropTypes.string,
  shareEmailPackageSubjectText: PropTypes.string,
  shareEmailPackageUrlLink: PropTypes.string.isRequired,
  soldOutButtonText: PropTypes.string,
  title: PropTypes.string.isRequired,
  tournament: PropTypes.string.isRequired,
  waitingListLink: PropTypes.object,
  waitingListText: PropTypes.string,
  preEventStopoverItinerary: PropTypes.shape({
    itineraryDescription: PropTypes.string.isRequired,
    itinerary: PropTypes.arrayOf(itinerarySideBarDayPropTypes).isRequired,
  }),
  perPersonText: PropTypes.string,
  postEventStopoverItinerary: PropTypes.shape({
    itineraryDescription: PropTypes.string.isRequired,
    itinerary: PropTypes.arrayOf(itinerarySideBarDayPropTypes).isRequired,
  }),
  abandonBasketEnabled: PropTypes.bool,
  abandonBasketModal: abandonBasketModalLabelProps,
  abandonBasketApi: PropTypes.string,
  tourKey: PropTypes.string,
  activeOnLoad: PropTypes.bool,
  fromPriceText: PropTypes.string,
  totalPriceText: PropTypes.string,
});

const defaultProps = {
  bookNowButtonText: 'Book now',
  bookNowButtonUrl: null,
  buttonText: 'Itinerary',
  callToBookButtonText: null,
  dayText: 'Day',
  perPersonText: 'pp',
  pricePerCurrency: [],
  description: null,
  handleToggleClick: null,
  headerText: 'Your itinerary',
  hideBookButton: false,
  isCallToBook: false,
  isSoldOut: false,
  isUpdated: false,
  priceTotal: null,
  shareButtonText: 'Book now',
  shareEmailPackageBodyText: '',
  shareEmailPackageSubjectText: '',
  soldOutButtonText: 'Sold Out',
  waitingListLink: null,
  waitingListText: '',
  preEventStopoverItinerary: null,
  postEventStopoverItinerary: null,
  abandonBasketEnabled: false,
  abandonBasketModal: undefined,
  abandonBasketApi: '',
  tourKey: '',
  activeOnLoad: false,
  fromPriceText: 'From',
  totalPriceText: 'Total Price',
};

class ItinerarySidebar extends Component {
  constructor(props) {
    super(props);

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleBookNowClick = this.handleBookNowClick.bind(this);
    this.handleCurrencyChange = this.handleCurrencyChange.bind(this);

    const currentCurrency = getInitialCurrencyState(props.pricePerCurrency);
    const currencyPriceSetting =
      props.pricePerCurrency.find((p) => p.currency === currentCurrency.code) ||
      props.pricePerCurrency[0];
    this.state = {
      isActive: props.activeOnLoad && window.innerWidth >= breakpoints.desktop,
      showPartialAnimation: true,
      isAbandonBasketModalOpen: false,
      currentCurrencyCodeAndSymbol: currentCurrency,
      currencyPriceSetting,
    };
  }

  handleBookNowClick() {
    if (this.props.abandonBasketEnabled) {
      this.setState({ isAbandonBasketModalOpen: true });
    } else {
      window.location.href = this.props.bookNowButtonUrl;
    }
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({ isActive: false });
    }, 1000 * 2);
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.currentCurrencyCodeAndSymbol.code !== this.state.currentCurrencyCodeAndSymbol.code
    ) {
      const newCurrencyPriceSettings = this.props.pricePerCurrency.find(
        (currencyItem) => currencyItem.currency === this.state.currentCurrencyCodeAndSymbol.code
      );
      if (newCurrencyPriceSettings)
        this.setState({ currencyPriceSetting: newCurrencyPriceSettings });
      else console.error('Currency not found from current currency chosen to currency from model');
    }
  }

  componentWillUnmount() {
    window.removeEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, this.handleCurrencyChange);
  }

  handleCurrencyChange(event) {
    const newCurrencyCode = event?.detail?.code;
    if (newCurrencyCode) {
      this.setState({ currentCurrencyCodeAndSymbol: event.detail });
    } else console.error('Currency data not found for:', newCurrencyCode);
  }

  handleToggleClick() {
    this.setState((state) => ({
      isActive: !state.isActive,
      showPartialAnimation: false,
    }));
    if (this.props.handleToggleClick) this.props.handleToggleClick();
  }

  static renderItineraryList(title, itinerary, dayText) {
    return (
      <>
        {title && <h3 className="ItinerarySidebar_listHeader">{title}</h3>}
        <ul className="ItinerarySidebar_list">
          {itinerary.map((itineraryDay) => (
            <li key={itineraryDay.day} className="ItinerarySidebar_item">
              <div className="ItinerarySidebar_day">
                <div className="ItinerarySidebar_eventDay">{`${dayText} ${itineraryDay.day}`}</div>
                <div className="ItinerarySidebar_eventDate">
                  {moment(itineraryDay.date).format(INPUT_DATE_FORMAT)}
                </div>
              </div>
              <div className="ItinerarySidebar_eventDetails">
                {itineraryDay.items.map((item) => (
                  <div className="ItinerarySidebar_eventDescription" key={item.key}>
                    {item.content}
                    <div className="ItinerarySidebar_eventType">
                      <Icon
                        blockName="ItinerarySidebar_eventIcon"
                        iconSource={IconSource.UI_ASSET}
                        value={item.type}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  }

  render() {
    const { isCallToBook, isSoldOut, waitingListLink } = this.props;

    const containerClasses = classNames('ItinerarySidebar', {
      active: this.state.isActive,
      updated: this.props.isUpdated,
      partial: this.state.showPartialAnimation,
    });

    const shareEmailPackageUrlLink = `${window.location.origin}${this.props.shareEmailPackageUrlLink}`;
    const showPriceTotal = !!(this.props.priceTotal && !isCallToBook);

    return (
      <>
        <aside className={containerClasses}>
          {/* Sidebar lip & toggle */}
          <div className="ItinerarySidebar_lip">
            <button
              type="button"
              onClick={this.handleToggleClick}
              className="ItinerarySidebar_toggleButton"
            >
              <svg
                preserveAspectRatio="xMinYMin meet"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                className="ItinerarySidebar_toggleTab ItinerarySidebar_toggleTab-left"
              >
                <path
                  d="M0,20a15.1,15.1,0,0,0,15.094-15.1V4.874A4.871,4.871,0,0,1,19.963,0H20V20Z"
                  fill="currentColor"
                />
              </svg>
              <span className="ItinerarySidebar_toggleText">{this.props.buttonText}</span>
              <svg
                preserveAspectRatio="xMaxYMax meet"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                className="ItinerarySidebar_toggleTab ItinerarySidebar_toggleTab-right"
              >
                <path
                  d="M0,20V0H.037A4.871,4.871,0,0,1,4.906,4.874v.021A15.1,15.1,0,0,0,20,20Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
          <div className="ItinerarySidebar_content">
            {/* Mobile toggle */}
            <button
              type="button"
              className="ItinerarySidebar_inlineToggleButton"
              onClick={this.handleToggleClick}
            >
              <Icon blockName="ItinerarySidebar_inlineToggleIcon" id="cross" />
            </button>
            {/* Itinerary header */}
            <h2 className="ItinerarySidebar_heading">{this.props.headerText}</h2>
            <div className="ItinerarySidebar_header">
              <div className="ItinerarySidebar_tournament">{this.props.tournament}</div>
              <h3 className="ItinerarySidebar_title">{this.props.title}</h3>
              {this.props.description && (
                <div className="ItinerarySidebar_description">{this.props.description}</div>
              )}
              <div className="ItinerarySidebar_packageDate">
                {moment(this.props.fromDate).format(DISPLAY_DATE_FORMAT_LONG_WITH_DAY)} -{' '}
                {moment(this.props.toDate).format(DISPLAY_DATE_FORMAT_LONG_WITH_DAY)}
              </div>
            </div>
            {/* Itinerary lists */}
            {this.props.preEventStopoverItinerary &&
              ItinerarySidebar.renderItineraryList(
                this.props.preEventStopoverItinerary.itineraryDescription,
                this.props.preEventStopoverItinerary.itinerary,
                this.props.dayText
              )}
            {ItinerarySidebar.renderItineraryList(
              this.props.preEventStopoverItinerary || this.props.postEventStopoverItinerary
                ? this.props.title
                : null,
              this.props.itinerary,
              this.props.dayText
            )}
            {this.props.postEventStopoverItinerary &&
              ItinerarySidebar.renderItineraryList(
                this.props.postEventStopoverItinerary.itineraryDescription,
                this.props.postEventStopoverItinerary.itinerary,
                this.props.dayText
              )}
            {/* Price */}
            {this.props.pricePerCurrency && !this.props.priceTotal && (
              <div className="ItinerarySidebar_price">
                <div className="ItinerarySidebar_priceLabel">{this.props.fromPriceText}</div>
                <div className="ItinerarySidebar_priceValue">
                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                  {this.state.currencyPriceSetting.formattedPrice}
                  {this.props.perPersonText}
                </div>
              </div>
            )}

            {showPriceTotal && (
              <div className="ItinerarySidebar_price">
                <div className="ItinerarySidebar_priceLabel">{this.props.totalPriceText}</div>
                <div className="ItinerarySidebar_priceValue">
                  {this.state.currentCurrencyCodeAndSymbol.symbol}
                  {formatPrice(this.props.priceTotal)}
                </div>
              </div>
            )}

            {/* CTA */}
            {this.props.bookNowButtonUrl && (
              <div className="ItinerarySidebar_cta">
                <div className="ItinerarySidebar_ctaButton">
                  <Button
                    blockName="ItinerarySidebar_shareButton"
                    elementType="link"
                    modifier="ghost"
                    text={this.props.shareButtonText}
                    url={`mailto:?subject=${
                      this.props.shareEmailPackageSubjectText
                        ? this.props.shareEmailPackageSubjectText
                        : ''
                    }&body=${
                      this.props.shareEmailPackageBodyText
                        ? this.props.shareEmailPackageBodyText
                        : ''
                    } ${shareEmailPackageUrlLink}`}
                  />
                </div>
                {!this.props.priceTotal && (
                  <div className="ItinerarySidebar_ctaButton">
                    {this.props.isSoldOut && (
                      <span className="ItinerarySidebar_soldOut">
                        {this.props.soldOutButtonText}
                      </span>
                    )}
                    {isCallToBook && (
                      <span className="ItinerarySidebar_callToBook">
                        {this.props.callToBookButtonText}
                      </span>
                    )}
                    {waitingListLink != null && (
                      <Button
                        blockName="ItinerarySidebar_bookButton"
                        elementType="link"
                        modifier="fill"
                        text={this.props.waitingListText}
                        url={waitingListLink.url}
                      />
                    )}
                    {!isSoldOut &&
                      !isCallToBook &&
                      !this.props.hideBookButton &&
                      waitingListLink === null && (
                        <Button
                          blockName="ItinerarySidebar_bookButton"
                          modifier="fill"
                          text={this.props.bookNowButtonText}
                          handleClick={this.handleBookNowClick}
                        />
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        </aside>
        {this.props.abandonBasketEnabled && (
          <AbandonBasketModal
            bookNowUrl={this.props.bookNowButtonUrl}
            isOpen={this.state.isAbandonBasketModalOpen}
            labels={this.props.abandonBasketModal}
            onClose={() => {
              this.setState({ isAbandonBasketModalOpen: false });
            }}
            submitApi={this.props.abandonBasketApi}
            tourKey={this.props.tourKey}
          />
        )}
      </>
    );
  }
}

ItinerarySidebar.propTypes = propTypes;
ItinerarySidebar.defaultProps = defaultProps;

export default ItinerarySidebar;
